import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";

/**
 * Signup Redesign · Step 2 of 3 — Compact Album Selection.
 *
 * Text-only checklist of the fixed World Cup album line-up owner
 * dictated verbatim. NO album cover images, NO variant matrix, NO
 * missing-sticker entry step. When a row is checked, an inline
 * radio pair asks whether it's Completed or Still Missing Stickers.
 *
 * Missing-sticker discovery is DEFERRED to the dashboard by design.
 * The dashboard shows a subtle prompt on any album marked "Still
 * Missing Stickers" so the user can fill in the list later without
 * blocking sign-up.
 *
 * Post-submit routing:
 *   • registration entry     → /dashboard
 *   • certificate_claim entry → /dashboard/claim/success?cert=…
 *     (celebratory ClaimSuccess page — never the generic Congrats)
 */
const ALBUM_LIST = [
  { key: "2026-soft", label: "2026 World Cup — Soft Cover", year: 2026, edition: "soft" },
  { key: "2026-hard", label: "2026 World Cup — Hard Cover", year: 2026, edition: "hard" },
  { key: "2022-soft", label: "2022 Qatar — Soft Cover", year: 2022, edition: "soft" },
  { key: "2022-hard", label: "2022 Qatar — Hard Cover", year: 2022, edition: "hard" },
  { key: "2018-soft", label: "2018 Russia — Soft Cover", year: 2018, edition: "soft" },
  { key: "2018-hard", label: "2018 Russia — Hard Cover", year: 2018, edition: "hard" },
  { key: "2014-soft", label: "2014 Brazil — Soft Cover", year: 2014, edition: "soft" },
  { key: "2014-hard", label: "2014 Brazil — Hard Cover", year: 2014, edition: "hard" },
  { key: "2010",     label: "2010 South Africa", year: 2010, edition: null },
  { key: "2006",     label: "2006 Germany", year: 2006, edition: null },
  { key: "2002",     label: "2002 South Korea & Japan", year: 2002, edition: null },
  { key: "1998",     label: "1998 France", year: 1998, edition: null },
  { key: "1994",     label: "1994 United States", year: 1994, edition: null },
  { key: "1990",     label: "1990 Italy", year: 1990, edition: null },
  { key: "1986",     label: "1986 Mexico", year: 1986, edition: null },
  { key: "1982",     label: "1982 Spain", year: 1982, edition: null },
  { key: "1978",     label: "1978 Argentina", year: 1978, edition: null },
  { key: "1974",     label: "1974 West Germany", year: 1974, edition: null },
  { key: "1970",     label: "1970 Mexico", year: 1970, edition: null },
];

/**
 * Resolve a hardcoded list entry to a real `catalog_album_variant_id`
 * from the backend catalog payload. After the CX-Gate seed migration
 * ran, every one of the 19 approved rows resolves to a real variant
 * (2022/2018/2014 get Soft+Hard variants; 2010–1970 get one variant
 * each). If a variant is somehow still missing on a fresh install
 * the row is left disabled — but the shipped preview never renders
 * a "Coming soon" pill anymore.
 */
function pickVariantForRow(row, catalog) {
  // Every release matching this row's year — there can be multiple
  // (e.g. two 2022 releases exist in the catalog).
  const releases = (catalog?.releases || []).filter((r) => r.year === row.year);
  for (const rel of releases) {
    const variants = rel.variants || [];
    if (row.edition) {
      // Signup Redesign · CX Gate — match by both edition_type and
      // name to survive different seeding conventions ("soft" vs
      // "softcover"). If no match on this release, keep scanning the
      // remaining same-year releases before giving up.
      const wantSoft = row.edition === "soft";
      const found = variants.find((v) => {
        const et = (v.edition_type || "").toLowerCase();
        const nm = (v.name || "").toLowerCase();
        return wantSoft
          ? et.includes("soft") || nm.includes("soft")
          : et.includes("hard") || nm.includes("hard");
      });
      if (found) return found;
      // NOTE: purposefully do NOT fall through to "any variant" when
      // the row has an edition constraint — that would silently map
      // a Soft Cover row to a Hard Cover variant.
      continue;
    }
    // No edition constraint — take the first active variant.
    if (variants.length > 0) return variants[0];
  }
  return null;
}

export default function OnboardingAlbumsCompact() {
  const navigate = useNavigate();
  const { collector, refresh } = useCollectorAuth();

  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [selections, setSelections] = useState({}); // { [key]: "completed" | "missing" }
  const [busy, setBusy] = useState(false);

  const ob = collector?.onboarding || {};
  const isClaim = ob.entry_type === "certificate_claim";
  const certNumber = ob.entry_cert_number;

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/onboarding/albums/catalog");
        setCatalog(r.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data) || "Could not load albums.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Map each fixed row → a real variant (or null when the catalog row
  // is not yet published).
  const rowVariants = useMemo(() => {
    const out = {};
    for (const row of ALBUM_LIST) {
      out[row.key] = pickVariantForRow(row, catalog);
    }
    return out;
  }, [catalog]);

  const ownedVariantIds = useMemo(() => {
    const s = new Set();
    for (const rel of (catalog?.releases || [])) {
      for (const v of rel.variants || []) {
        if (v.already_owned_album_id) s.add(v.album_variant_id);
      }
    }
    return s;
  }, [catalog]);

  const selectableCount = Object.keys(selections).length;

  const toggle = (key) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (prev[key]) delete next[key];
      else next[key] = "missing"; // default when adding
      return next;
    });
  };

  const setStatus = (key, value) => {
    setSelections((prev) => ({ ...prev, [key]: value }));
  };

  const goContinue = async () => {
    setBusy(true);
    try {
      // Convert selections to backend picks (only rows with a real
      // variant AND not already owned).
      const picks = [];
      for (const [key, status] of Object.entries(selections)) {
        const v = rowVariants[key];
        if (!v) continue;
        if (ownedVariantIds.has(v.album_variant_id)) continue;
        picks.push({
          album_variant_id: v.album_variant_id,
          complete: status === "completed" ? "yes" : "not_yet",
        });
      }
      if (picks.length > 0) {
        await api.post("/me/onboarding/albums/bulk", { picks });
      }
      // Signup Redesign — skip completion+help entirely. Log a `no`
      // decision on the help step so the backend timeline stays clean,
      // then finalize onboarding.
      try {
        await api.post("/me/onboarding/help/submit", { wants_help: false, inquiries: [] });
      } catch (_) { /* the endpoint is idempotent; safe to ignore */ }
      try {
        await api.post("/me/onboarding/complete");
      } catch (_) { /* idempotent */ }

      // Navigate FIRST so the OnboardingLayout doesn't race the
      // "status=complete" refresh and short-circuit certificate-claim
      // signups into /dashboard.
      //
      // Phase C1 — for a certificate-claim entry we route back to the
      // ClaimCodeForm (`/claim?cert=…`). Creating an account does NOT
      // itself claim the certificate — the user still has to enter the
      // one-time claim code from the physical certificate insert. Only
      // after the claim endpoint succeeds does the flow reach
      // `/dashboard/claim/success`.
      if (isClaim && certNumber) {
        navigate(`/dashboard/claim?cert=${encodeURIComponent(certNumber)}`, { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
      // Refresh happens after navigation so downstream pages see the
      // updated onboarding.status.
      refresh().catch(() => { /* non-fatal */ });
    } catch (e) {
      toast.error(formatApiError(e.response?.data) || "Could not save albums.");
    } finally {
      setBusy(false);
    }
  };

  const goBack = async () => {
    try {
      await api.post("/me/onboarding/step", { step: "profile" });
      await refresh();
    } catch (_) { /* ignore */ }
    navigate("/onboarding/profile");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[var(--smoke)]" data-testid="onboarding-albums-loading">
        <Loader2 className="animate-spin mr-3" size={22} /> Loading albums…
      </div>
    );
  }

  const finishLabel = isClaim ? "Finish & enter claim code" : "Finish & go to dashboard";

  return (
    <div data-testid="onboarding-albums-compact">
      <div className="kicker kicker-yellow">Step 2 of 2</div>
      <h1 className="font-serif text-3xl sm:text-4xl mt-3">
        Which World Cup albums do you have?
      </h1>
      <p className="mt-3 text-[var(--smoke)] max-w-2xl">
        Check every album you own or are working on. Then say whether it&apos;s
        already complete or still missing stickers. You can always add more later.
      </p>

      <div className="mt-8 rounded-3xl border-2 border-[var(--ink)] bg-white overflow-hidden shadow-[6px_6px_0_var(--panini-yellow)]" data-testid="onboarding-albums-list">
        <ul className="divide-y divide-[var(--ink)]/10">
          {ALBUM_LIST.map((row) => {
            const variant = rowVariants[row.key];
            const owned = variant ? ownedVariantIds.has(variant.album_variant_id) : false;
            const disabled = !variant; // no catalog row → coming soon
            const checked = Boolean(selections[row.key]);
            const status = selections[row.key]; // "completed" | "missing"
            return (
              <li
                key={row.key}
                data-testid={`onboarding-album-row-${row.key}`}
                data-checked={checked ? "true" : "false"}
                data-owned={owned ? "true" : "false"}
                data-disabled={disabled ? "true" : "false"}
                className={`px-4 md:px-5 py-3 ${
                  disabled ? "opacity-55" : owned ? "opacity-70 bg-[var(--panini-yellow-soft)]/25" : ""
                }`}
              >
                <label className={`flex items-center gap-3 cursor-pointer ${disabled || owned ? "cursor-not-allowed" : ""}`}>
                  <input
                    type="checkbox"
                    className="w-5 h-5 accent-[var(--panini-red)]"
                    data-testid={`onboarding-album-check-${row.key}`}
                    checked={owned ? true : checked}
                    disabled={disabled || owned}
                    onChange={() => toggle(row.key)}
                  />
                  <span className="flex-1 text-[15px] md:text-base font-semibold text-[var(--ink)]">
                    {row.label}
                  </span>
                  {owned && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--ink)]/25 bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-[var(--ink)]/70" data-testid={`onboarding-album-owned-${row.key}`}>
                      <Check size={11} /> In your collection
                    </span>
                  )}
                  {disabled && (
                    <span className="inline-flex items-center rounded-full border border-[var(--ink)]/20 bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-[var(--smoke)]" data-testid={`onboarding-album-soon-${row.key}`}>
                      Coming soon
                    </span>
                  )}
                </label>

                {/* Inline status radios — only when the row is checked
                    and not already owned/disabled. */}
                {checked && !owned && !disabled && (
                  <div
                    className="mt-2 ml-8 flex flex-wrap items-center gap-2 text-xs"
                    data-testid={`onboarding-album-status-${row.key}`}
                  >
                    <StatusPill
                      active={status === "completed"}
                      onClick={() => setStatus(row.key, "completed")}
                      testid={`onboarding-album-status-${row.key}-completed`}
                    >
                      Completed
                    </StatusPill>
                    <StatusPill
                      active={status === "missing"}
                      onClick={() => setStatus(row.key, "missing")}
                      testid={`onboarding-album-status-${row.key}-missing`}
                    >
                      Still Missing Stickers
                    </StatusPill>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div
        className="mt-6 rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] px-4 py-3 text-sm text-[var(--ink)] font-bold"
        data-testid="onboarding-albums-summary"
      >
        {selectableCount === 0
          ? "No albums selected yet — that's fine, you can add them later from your dashboard."
          : selectableCount === 1
          ? "1 album ready to add to your collection."
          : `${selectableCount} albums ready to add to your collection.`}
      </div>

      <p className="mt-3 text-[11px] text-[var(--smoke)]">
        We do <strong>not</strong> ask for missing sticker numbers here. Any album
        you mark <em>Still Missing Stickers</em> will show a friendly prompt on your
        dashboard so you can add your list later.
      </p>

      <div className="mt-8 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <button
          data-testid="onboarding-albums-back"
          onClick={goBack}
          className="btn-ghost inline-flex items-center gap-2"
        >
          <ArrowLeft size={18} /> Back
        </button>
        <button
          data-testid="onboarding-albums-continue"
          onClick={goContinue}
          disabled={busy}
          className="btn-red inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? "Saving…" : finishLabel} <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

function StatusPill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      className={`rounded-full border-2 border-[var(--ink)] px-3 py-1 font-black uppercase tracking-widest text-[10px] transition-colors ${
        active
          ? "bg-[var(--panini-red)] text-white"
          : "bg-white text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
      }`}
    >
      {children}
    </button>
  );
}
